import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useResetPassword } from "../../../hooks/useAPI";
import {
  AuthShell,
  AuthBanner,
  AuthInput,
  AuthButton,
  PasswordRuleChecklist,
  TokenStatePage,
  allRulesMet,
  PWD_RULES,
} from "./_primitives";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const resetPassword = useResetPassword();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const allMet = allRulesMet(password);
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = Boolean(token) && allMet && passwordsMatch && !resetPassword.isPending;

  useEffect(() => {
    if (!token) return;
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!done) return;
    const id = window.setTimeout(() => navigate("/login"), 3000);
    return () => window.clearTimeout(id);
  }, [done, navigate]);

  if (!token) {
    return (
      <AuthShell pageTitle={t("resetPassword.linkInvalidPageTitle")} pageDescription={t("resetPassword.linkInvalidPageDescription")}>
        <TokenStatePage
          status="no-token"
          noTokenTitle={t("resetPassword.linkInvalidTitle")}
          noTokenBody={<>{t("resetPassword.linkInvalidBody")}</>}
          noTokenAction={
            <Link to="/login" className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2">
              {t("common.actions.backToSignIn")}
            </Link>
          }
        />
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell pageTitle={t("resetPassword.successPageTitle")} pageDescription={t("resetPassword.successPageDescription")}>
        <TokenStatePage
          status="success"
          successTitle={t("resetPassword.successTitle")}
          successBody={<>{t("resetPassword.successBody")}</>}
          successAction={
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {t("resetPassword.successAction")}
            </button>
          }
        />
      </AuthShell>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (resetPassword.isPending) return;
    setError("");
    if (!allMet) { setError(t("resetPassword.errors.rulesNotMet")); return; }
    if (!passwordsMatch) { setError(t("resetPassword.errors.mismatch")); return; }

    resetPassword.mutate(
      { token, password },
      {
        onSuccess: () => setDone(true),
        onError: (err) => {
          const lowered = err instanceof Error ? err.message.toLowerCase() : "";
          if (lowered.includes("expired") || lowered.includes("invalid")) {
            setError(t("resetPassword.errors.invalidLink"));
          } else {
            setError(t("resetPassword.errors.generic"));
          }
        },
      },
    );
  };

  return (
    <AuthShell pageTitle={t("resetPassword.pageTitle")} pageDescription={t("resetPassword.pageDescription")}>
      <div className="space-y-8">
        <header className="text-center">
          <h1 className="editorial-display">{t("resetPassword.title")}</h1>
          <p className="editorial-subtitle">
            {t("resetPassword.subtitle")}
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <AuthBanner variant="error">{error}</AuthBanner>}

          <div>
            <AuthInput
              label={t("resetPassword.passwordLabel")}
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("resetPassword.passwordPlaceholder")}
              autoComplete="new-password"
              autoCapitalize="off"
              spellCheck={false}
              disabled={resetPassword.isPending}
              leadingIcon={<Lock className="w-4 h-4" aria-hidden="true" />}
              withPasswordToggle
              aria-invalid={password.length > 0 && !allMet ? "true" : undefined}
              aria-describedby="resetpassword-pwd-rules"
            />
            <PasswordRuleChecklist id="resetpassword-pwd-rules" password={password} rules={PWD_RULES} />
          </div>

          <AuthInput
            label={t("resetPassword.confirmLabel")}
            type="password"
            name="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("resetPassword.confirmPlaceholder")}
            autoComplete="new-password"
            autoCapitalize="off"
            spellCheck={false}
            disabled={resetPassword.isPending}
            leadingIcon={<Lock className="w-4 h-4" aria-hidden="true" />}
            aria-invalid={confirm.length > 0 && !passwordsMatch ? "true" : undefined}
            error={confirm.length > 0 && !passwordsMatch ? t("password.match") : undefined}
          />

          <AuthButton
            type="submit"
            variant="primary"
            fullWidth
            loading={resetPassword.isPending}
            loadingLabel={t("resetPassword.submitLoading")}
            disabled={!canSubmit}
          >
            {t("resetPassword.submit")}
          </AuthButton>

          <div className="text-center pt-2">
            <Link to="/login" className="auth-tertiary">
              {t("resetPassword.backToSignIn")}
            </Link>
          </div>
        </form>
      </div>
    </AuthShell>
  );
}
